import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

function FullNewsRead() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [article, setArticle] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [shorts, setShorts] = useState([]);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [playingShort, setPlayingShort] = useState(null);

  useEffect(() => {
    fetchNewsDetail();
    fetchShorts();
    window.scrollTo(0, 0);
  }, [id]);

  const fetchNewsDetail = async () => {
    try {
      setLoading(true);
      const response = await fetch(`https://backend-gocq.onrender.com/api/news/${id}`);
      const data = await response.json();
      if (data.success) {
        setArticle(data.data.news);
        setRelatedNews(data.data.relatedNews || []);
      } else {
        alert('समाचार नहीं मिला');
        navigate('/');
      }
    } catch (error) {
      console.error('Error fetching news:', error);
      alert('समाचार लोड करने में त्रुटि');
    } finally {
      setLoading(false);
    }
  };

  const fetchShorts = async () => {
    try {
      const res = await fetch(`https://backend-gocq.onrender.com/api/news?type=shorts&limit=6`);
      const data = await res.json();
      if (data.success) setShorts(data.data || []);
    } catch (err) {
      console.error('Error fetching shorts:', err);
    }
  };

  const getTimeAgo = (date) => {
    const diffMs   = Date.now() - new Date(date);
    const diffMins  = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays  = Math.floor(diffMs / 86400000);
    if (diffMins  < 60) return `${diffMins} मिनट पहले`;
    if (diffHours < 24) return `${diffHours} घंटे पहले`;
    if (diffDays  <  7) return `${diffDays} दिन पहले`;
    return new Date(date).toLocaleDateString('hi-IN');
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800&q=80';
    if (imagePath.startsWith('http')) return imagePath;
    return `https://backend-gocq.onrender.com${imagePath}`;
  };

  const getVideoInfo = (videoPath) => {
    if (!videoPath) return null;
    const url    = videoPath.startsWith('http') ? videoPath : `https://backend-gocq.onrender.com${videoPath}`;
    const ytId   = (url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/) ||
                    url.match(/[?&]v=([a-zA-Z0-9_-]{11})/)      ||
                    url.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/))?.[1];
    if (ytId) return { type: 'youtube', embedUrl: `https://www.youtube.com/embed/${ytId}?rel=0&modestbranding=1` };
    const vimeo  = url.match(/vimeo\.com\/(\d+)/);
    if (vimeo)  return { type: 'vimeo',   embedUrl: `https://player.vimeo.com/video/${vimeo[1]}` };
    return { type: 'direct', embedUrl: url };
  };

  const formatViews = (views) => {
    if (views >= 1000000) return `${(views / 1000000).toFixed(1)}M`;
    if (views >= 1000)    return `${(views / 1000).toFixed(1)}K`;
    return views;
  };

  const handleShare = (platform) => {
    const url  = window.location.href;
    const text = article?.title || '';
    const map  = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter:  `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
      whatsapp: `https://wa.me/?text=${text} ${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    };
    if (map[platform]) window.open(map[platform], '_blank');
    setShowShareMenu(false);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('लिंक कॉपी हो गया!');
    setShowShareMenu(false);
  };

  const goToNews = (newsId) => { navigate(`/news/${newsId}`); window.scrollTo(0, 0); };

  // ── Reusable ratio-box wrapper ──────────────────────────────────────────────
  // paddingTop trick: keeps a fixed aspect ratio regardless of container width.
  const RatioBox = ({ ratio = '56.25%', children, style = {} }) => (
    <div style={{ position: 'relative', width: '100%', paddingTop: ratio, overflow: 'hidden', ...style }}>
      <div style={{ position: 'absolute', inset: 0 }}>{children}</div>
    </div>
  );

  // ── CoverImg: always fills its parent without stretching ────────────────────
  const CoverImg = ({ src, alt, style = {} }) => (
    <img
      src={src}
      alt={alt}
      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', ...style }}
      onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800&q=80'; }}
    />
  );

  // ── IframeEmbed ──────────────────────────────────────────────────────────────
  const IframeEmbed = ({ src, title }) => (
    <RatioBox ratio="56.25%">
      <iframe
        src={src}
        title={title}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        style={{ width: '100%', height: '100%', border: 0 }}
      />
    </RatioBox>
  );

  // ── ShortVideoCard ───────────────────────────────────────────────────────────
  const ShortVideoCard = ({ short, playKey }) => {
    const vi        = getVideoInfo(short.video);
    const isPlaying = playingShort === playKey;
    return (
      <div onClick={() => setPlayingShort(isPlaying ? null : playKey)}
        style={{ cursor: 'pointer', width: '100%', height: '100%' }}>
        {isPlaying && vi?.type === 'direct' ? (
          <video src={vi.embedUrl} autoPlay controls
            style={{ width: '100%', height: '100%', objectFit: 'cover', background: '#000', display: 'block' }} />
        ) : isPlaying ? (
          <iframe src={`${vi.embedUrl}&autoplay=1`} title={short.title} frameBorder="0"
            allow="autoplay; encrypted-media" allowFullScreen
            style={{ width: '100%', height: '100%', border: 0 }} />
        ) : (
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <CoverImg src={getImageUrl(short.image)} alt={short.title} />
            {/* play overlay */}
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.28)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: 34, height: 34, background: 'rgba(255,255,255,0.93)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="13" height="13" fill="#f00400" viewBox="0 0 20 20">
                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                </svg>
              </div>
            </div>
            <div style={{ position: 'absolute', bottom: 6, left: 6 }}>
              <span style={{ background: 'linear-gradient(135deg,#f00400,#ff6b35)', color: '#fff', fontSize: '0.55rem', fontWeight: 700, padding: '2px 6px', borderRadius: 999 }}>
                Shorts
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#f00400' }}></div>
          <p className="text-gray-600">समाचार लोड हो रहा है...</p>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">समाचार नहीं मिला</h2>
          <button onClick={() => navigate('/')} className="px-6 py-3 text-white rounded-lg font-semibold" style={{ backgroundColor: '#f00400' }}>
            होम पर जाएं
          </button>
        </div>
      </div>
    );
  }

  const videoInfo = getVideoInfo(article.video);
  const hasVideo  = !!videoInfo;
  const hasImage  = !!article.image;

  return (
    <div className="min-h-screen bg-gray-50">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Tiro+Devanagari+Hindi:ital@0;1&family=Mukta:wght@300;400;600;700;800&display=swap');
        * { font-family: 'Mukta', sans-serif; }
        .headline-font { font-family: 'Tiro Devanagari Hindi', serif; }

        .related-card { transition: all 0.2s ease; }
        .related-card:hover { transform: translateX(4px); }

        .video-badge {
          background: linear-gradient(135deg, #7c3aed, #5b21b6);
          color: white; font-size: 0.7rem; font-weight: 700;
          padding: 3px 10px; border-radius: 999px;
          display: inline-flex; align-items: center; gap: 4px;
        }

        /* Shorts horizontal scroll */
        .shorts-scroll { display: flex; gap: 10px; overflow-x: auto; padding-bottom: 6px; scrollbar-width: thin; scrollbar-color: #f00400 #f1f1f1; }
        .shorts-scroll::-webkit-scrollbar { height: 4px; }
        .shorts-scroll::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
        .shorts-scroll::-webkit-scrollbar-thumb { background: #f00400; border-radius: 10px; }

        /* short card fixed size — overflow hidden clips the image */
        .short-card-h { flex-shrink: 0; width: 110px; height: 180px; border-radius: 12px; overflow: hidden; transition: transform 0.2s; }
        .short-card-h:hover { transform: scale(1.04); }

        .shorts-icon { width: 26px; height: 26px; background: linear-gradient(135deg, #f00400, #ff6b35); border-radius: 7px; display: flex; align-items: center; justify-content: center; }

        /* article hero image — fixed 56% ratio, never stretches */
        .hero-img-wrap { position: relative; width: 100%; padding-top: 56.25%; overflow: hidden; border-radius: 0.75rem; }
        .hero-img-wrap img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; display: block; }

        /* related thumbnail — fixed 80×64 */
        .related-thumb { width: 96px; height: 80px; flex-shrink: 0; overflow: hidden; border-radius: 0.5rem; position: relative; }
        .related-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
      `}</style>

      {/* Breadcrumb */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span onClick={() => navigate('/')} className="cursor-pointer hover:text-red-600 transition-colors">होम</span>
            <span>›</span>
            <span className="text-red-600 font-semibold">{article.category}</span>
            <span>›</span>
            <span className="text-gray-400 truncate max-w-xs">{article.title}</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* ── Left: Article ── */}
          <div className="lg:col-span-2">

            {/* SHORTS — horizontal scroll */}
            {shorts.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm p-4 mb-6 border-t-4" style={{ borderColor: '#f00400' }}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="shorts-icon">
                    <svg width="13" height="13" fill="white" viewBox="0 0 24 24">
                      <path d="M17.77 10.32l-1.2-.5L18 9.06c1.84-.96 2.53-3.23 1.56-5.06s-3.23-2.53-5.06-1.56L6 6.94c-1.29.68-2.07 2.01-2 3.44.07 1.29.75 2.43 1.79 3.07L7 13.94l-1.43.75C3.73 15.65 3.04 17.92 4 19.75s3.23 2.53 5.06 1.56l8.5-4.5c1.29-.68 2.07-2.01 2-3.44-.07-1.29-.75-2.43-1.79-3.05zM10 14.5v-5l4.5 2.5-4.5 2.5z"/>
                    </svg>
                  </div>
                  <h3 className="font-bold text-gray-900">📱 Shorts</h3>
                  <span className="text-xs text-gray-400 ml-auto">← स्वाइप →</span>
                </div>
                <div className="shorts-scroll">
                  {shorts.map((short) => (
                    <div key={short._id} className="short-card-h">
                      <ShortVideoCard short={short} playKey={`main-${short._id}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Article */}
            <article className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="p-6 md:p-8">

                {/* Badges */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <div className="inline-block px-4 py-1.5 rounded-full text-white text-sm font-bold" style={{ backgroundColor: '#f00400' }}>
                    {article.category}
                  </div>
                  {hasVideo && (
                    <span className="video-badge">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                      </svg>
                      VIDEO
                    </span>
                  )}
                </div>

                {/* Title */}
                <h1 className="headline-font text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-4">
                  {article.title}
                </h1>

                {/* Description */}
                {article.description && (
                  <p className="text-xl text-gray-600 mb-6 leading-relaxed border-l-4 pl-4" style={{ borderColor: '#f00400' }}>
                    {article.description}
                  </p>
                )}

                {/* Meta */}
                <div className="flex flex-wrap items-center gap-4 py-4 border-y border-gray-100 mb-6">
                  <div className="flex items-center gap-2 text-gray-600 text-sm">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                    <span className="font-semibold">{article.author || 'Admin'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 text-sm">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                    <span>{new Date(article.createdAt).toLocaleDateString('hi-IN')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 text-sm">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                    </svg>
                    <span>{formatViews(article.views)} व्यूज</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap items-center gap-3 mb-6">
                  <button
                    onClick={() => setIsBookmarked(!isBookmarked)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                      isBookmarked ? 'bg-yellow-400 text-black' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <svg className="w-4 h-4" fill={isBookmarked ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                    </svg>
                    {isBookmarked ? 'सेव किया' : 'सेव करें'}
                  </button>

                  <div className="relative">
                    <button onClick={() => setShowShareMenu(!showShareMenu)}
                      className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-semibold text-sm"
                      style={{ backgroundColor: '#f00400' }}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                      </svg>
                      शेयर करें
                    </button>
                    {showShareMenu && (
                      <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-xl p-2 z-20 min-w-[180px] border">
                        {[
                          { label: '📘 Facebook', key: 'facebook' },
                          { label: '🐦 Twitter',  key: 'twitter'  },
                          { label: '💬 WhatsApp', key: 'whatsapp' },
                          { label: '💼 LinkedIn', key: 'linkedin' },
                        ].map(({ label, key }) => (
                          <button key={key} onClick={() => handleShare(key)}
                            className="flex items-center gap-2 w-full px-3 py-2 hover:bg-gray-100 rounded-lg text-sm text-left">
                            {label}
                          </button>
                        ))}
                        <button onClick={handleCopyLink}
                          className="flex items-center gap-2 w-full px-3 py-2 hover:bg-gray-100 rounded-lg text-sm text-left">
                          🔗 लिंक कॉपी करें
                        </button>
                      </div>
                    )}
                  </div>

                  <button onClick={() => navigate('/')}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-semibold text-sm hover:bg-gray-200 transition-colors">
                    ← वापस जाएं
                  </button>
                </div>

                {/* ── MEDIA SECTION — all images in ratio boxes ── */}
                <div className="mb-8">
                  {hasImage && (
                    <div className="hero-img-wrap mb-4">
                      <img
                        src={getImageUrl(article.image)}
                        alt={article.title}
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800&q=80'; }}
                      />
                    </div>
                  )}
                  {hasVideo && (
                    <div style={{ borderRadius: '0.75rem', overflow: 'hidden' }}>
                      {videoInfo.type === 'direct' ? (
                        <RatioBox ratio="56.25%">
                          <video src={videoInfo.embedUrl} controls preload="metadata"
                            style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#0a0a0a', display: 'block' }}>
                            आपका ब्राउज़र वीडियो सपोर्ट नहीं करता।
                          </video>
                        </RatioBox>
                      ) : (
                        <IframeEmbed src={videoInfo.embedUrl} title={article.title} />
                      )}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="text-gray-800 leading-loose text-lg whitespace-pre-line">
                  {article.content}
                </div>

                {/* Tags */}
                {article.tags && article.tags.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 mt-8 pt-6 border-t border-gray-100">
                    <span className="text-sm font-semibold text-gray-500">टैग्स:</span>
                    {article.tags.map((tag, index) => (
                      <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-red-50 hover:text-red-600 cursor-pointer transition-colors">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </article>
          </div>

          {/* ── Sidebar ── */}
          <div className="lg:col-span-1">
            <div className="sticky top-4 space-y-6">

              {/* Related News */}
              <div className="bg-white rounded-2xl shadow-sm p-6 border-t-4" style={{ borderColor: '#f00400' }}>
                <h3 className="headline-font text-xl font-bold text-gray-900 mb-5 flex items-center gap-2">
                  <span className="w-2 h-6 rounded-full" style={{ backgroundColor: '#f00400' }}></span>
                  संबंधित समाचार
                </h3>
                <div className="space-y-4">
                  {relatedNews.length > 0 ? (
                    relatedNews.map((news, index) => (
                      <div key={news._id}>
                        <div className="related-card group cursor-pointer" onClick={() => goToNews(news._id)}>
                          <div className="flex gap-3">
                            {/* fixed-size thumbnail — never stretches */}
                            <div className="related-thumb">
                              <img
                                src={getImageUrl(news.image)}
                                alt={news.title}
                                onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=400&q=60'; }}
                              />
                              {news.video && (
                                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)', borderRadius: '0.5rem' }}>
                                  <div style={{ width: 28, height: 28, background: 'rgba(255,255,255,0.9)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <svg className="w-3 h-3 ml-0.5" fill="#7c3aed" viewBox="0 0 20 20">
                                      <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                                    </svg>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1 mb-1">
                                <span className="text-xs px-2 py-0.5 rounded text-white font-semibold" style={{ backgroundColor: '#f00400' }}>
                                  {news.category}
                                </span>
                                {news.video && (
                                  <span className="text-xs px-1.5 py-0.5 rounded text-white font-semibold" style={{ backgroundColor: '#7c3aed' }}>
                                    वीडियो
                                  </span>
                                )}
                              </div>
                              <h4 className="font-bold text-sm text-gray-900 group-hover:text-red-600 transition-colors mt-1 line-clamp-2">
                                {news.title}
                              </h4>
                              <p className="text-xs text-gray-400 mt-1">{getTimeAgo(news.createdAt)}</p>
                            </div>
                          </div>
                        </div>
                        {index < relatedNews.length - 1 && <div className="border-b border-gray-100 mt-4"></div>}
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center py-4 text-sm">कोई संबंधित समाचार नहीं</p>
                  )}
                </div>
              </div>

              {/* Shorts in Sidebar (vertical list) */}
              {shorts.length > 0 && (
                <div className="bg-white rounded-2xl shadow-sm p-5 border-t-4" style={{ borderColor: '#f00400' }}>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="shorts-icon">
                      <svg width="13" height="13" fill="white" viewBox="0 0 24 24">
                        <path d="M17.77 10.32l-1.2-.5L18 9.06c1.84-.96 2.53-3.23 1.56-5.06s-3.23-2.53-5.06-1.56L6 6.94c-1.29.68-2.07 2.01-2 3.44.07 1.29.75 2.43 1.79 3.07L7 13.94l-1.43.75C3.73 15.65 3.04 17.92 4 19.75s3.23 2.53 5.06 1.56l8.5-4.5c1.29-.68 2.07-2.01 2-3.44-.07-1.29-.75-2.43-1.79-3.05zM10 14.5v-5l4.5 2.5-4.5 2.5z"/>
                      </svg>
                    </div>
                    <h3 className="font-bold text-gray-900 text-base">📱 Shorts</h3>
                  </div>
                  <div className="flex flex-col gap-4">
                    {shorts.slice(0, 4).map((short) => (
                      <div key={short._id} className="flex gap-3 items-center">
                        {/* sidebar short thumbnail: fixed 72×112 */}
                        <div style={{ width: 72, height: 112, borderRadius: 10, overflow: 'hidden', flexShrink: 0 }}>
                          <ShortVideoCard short={short} playKey={`sidebar-${short._id}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 line-clamp-2 leading-snug hover:text-red-600 cursor-pointer"
                            onClick={() => goToNews(short._id)}>
                            {short.title}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">{getTimeAgo(short.createdAt)}</p>
                          <span style={{ background: 'linear-gradient(135deg,#f00400,#ff6b35)', color: '#fff', fontSize: '0.55rem', fontWeight: 700, padding: '2px 7px', borderRadius: 999, display: 'inline-block', marginTop: 4 }}>
                            Shorts
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Subscribe */}
              <div className="bg-white rounded-2xl shadow-sm p-6 text-center border-t-4" style={{ borderColor: '#f00400' }}>
                <div className="mb-3" style={{ color: '#f00400' }}>
                  <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <h3 className="headline-font text-lg font-bold mb-1 text-gray-900">न्यूज अलर्ट पाएं</h3>
                <p className="text-xs text-gray-500 mb-4">सबसे पहले खबरें पाने के लिए सब्सक्राइब करें</p>
                <button className="w-full text-white px-4 py-2.5 rounded-full font-semibold text-sm hover:opacity-90 transition-opacity" style={{ backgroundColor: '#f00400' }}>
                  अभी सब्सक्राइब करें
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FullNewsRead;